import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL, getStatusStyle, languageIdToName, restrictSpecialCharacters, restrictSpecialCharactersMultilingual } from "@utils/constants";
import { toast } from "react-toastify";
import { fetchSentencesByFileId } from "@store/thunks/devnagriValidationAdminThunk";
import apiClient from "@/services/api/apiClient";

const SentenceValidationTableDevnagriValidator = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const fileStatus = useSelector((state) => state.devnagriValidator.fileStatus);
  const { targetLanguageName } = useSelector((state) => state.fileLanguage);
  const [editedSentences, setEditedSentences] = useState(new Set());
  const [inputValues, setInputValues] = useState({});
  const [validatedSentences, setValidatedSentences] = useState(new Set());
  const [finalCommitCompleted, setFinalCommitCompleted] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [clickReject, setClickReject] = useState(false);
  const [sentences, setSentences] = useState([]);
  const [rejectedSentences, setRejectedSentences] = useState([]);
  const { user, token, userId } = useSelector((state) => state.auth);
  const { selectedFile, selectedAssignedFileId, targetLanguageId, assignedFiles, selectedAssignedFolderId } = useSelector((state) => state.devnagriValidator);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });
  const { fileName } = useSelector((store) => store.fileMeta);

  const isFileAlreadyValidated = fileStatus === "DEV_VALIDATED" || fileStatus === "DEV_COMPLETED";


  // Render popup for warnings
  const renderPopup = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-200">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-[calc(100vw-2rem)] sm:w-4/5 md:w-3/4 max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 z-10">
          <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
            <span className="text-[#ec7026] shadow-sm">{popup.type === "success" ? "Success" : "Error"}</span> Message
          </h2>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-sm font-['Semantic/Strong'] text-gray-800">{popup.message}</div>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 bg-white flex justify-end">
          <button
            onClick={() => setPopup({ isOpen: false, type: "", message: "" })}
            className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            OK
          </button>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const fetchSentences = async () => {
      if (!selectedAssignedFileId) return;
      setLoading(true);
      try {
        const files = selectedAssignedFolderId ? (assignedFiles[selectedAssignedFolderId] || []) : [];
        const file = files.find(f => f.fileId === selectedAssignedFileId);
        if (!file) {
          toast.error("File data not found");
          setLoading(false);
          return;
        }

        if (clickReject) {
          // Fetch rejected sentences
          const result = await apiClient(
            `${BASE_URL}api/devUser/rejectedSentences?fileId=${file.fileId}&devUserId=${userId}&sourceLangId=${file.sourceId}&targetLangId=${file.targetId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                Accept: "*/*",
              },
              credentials: "include",
            }, navigate
          );
          console.log("[Rejected Sentences] API response:", result);
          if (result.status === "success") {
            setRejectedSentences(result.data || []);
          } else {
            setRejectedSentences([]);
            toast.error(result.message || "Failed to fetch rejected sentences");
          }
        } else {
          // Fetch all sentences
          const data = await dispatch(fetchSentencesByFileId(
            file.fileId,
            file.validatorUserId,
            file.sourceId,
            file.targetId,
            clickReject
          ));
          console.log("[All Sentences] Fetched data:", data);
          setSentences(data);
        }

        // Initialize input values and validated sentences
        const initialValues = {};
        const initialValidated = new Set();
        const data = clickReject ? rejectedSentences : sentences;
        data.forEach(sentence => {
          initialValues[sentence.sentenceId] = {
            cdacSentenceText: sentence.devSentenceText || sentence.cdacSentenceText || "",
            validatedTextGs: sentence.validatedTextDev || sentence.validatedTextGs || ""
          };
          if (sentence.validationStatusDev === "VALIDATED") {
            initialValidated.add(sentence.sentenceId);
          }
        });
        setInputValues(initialValues);
        setValidatedSentences(initialValidated);
        setEditedSentences(new Set());

        if (file.status === "DEV_VALIDATED") {
          setFinalCommitCompleted(true);
        }
      } catch (error) {
        console.error("[Fetch Sentences] Error:", error);
        setSentences([]);
        setRejectedSentences([]);
        toast.error("Failed to fetch sentences");
      } finally {
        setLoading(false);
      }
    };
    fetchSentences();
  }, [selectedAssignedFileId, dispatch, assignedFiles, selectedAssignedFolderId, clickReject, reloadTrigger]);

  const handleEditChange = (sentenceId, field, value, e) => {
    if (finalCommitCompleted) return;

    // Decide which restriction function to apply
    const isValid =
      field === "cdacSentenceText"
        ? restrictSpecialCharacters(value)
        : restrictSpecialCharactersMultilingual(value);

    if (isValid || value === "") {
      console.log("[handleEditChange] Editing:", { sentenceId, field, value });

      setInputValues(prev => ({
        ...prev,
        [sentenceId]: {
          ...prev[sentenceId],
          [field]: value
        }
      }));

      setEditedSentences(prev => new Set([...prev, sentenceId]));

      setSentences(prev =>
        prev.map(s =>
          s.sentenceId === sentenceId
            ? { ...s, validationStatusDev: "Edited" }
            : s
        )
      );

      setValidatedSentences(prev => {
        const newSet = new Set(prev);
        newSet.delete(sentenceId);
        return newSet;
      });
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only valid characters.",
      });

      if (e?.target) e.target.blur(); // safety check for e
    }
  };


  const handleValidate = async (sentence) => {
    if (finalCommitCompleted || loading) return;
    setLoading(true);

    try {
      const currentValues = inputValues[sentence.sentenceId] || {};
      const requestBody = {
        fileId: selectedAssignedFileId,
        sentenceId: sentence.sentenceId,
        targetLanguageId: sentence.targetLanguageId,
        sentenceText: currentValues.cdacSentenceText || sentence.cdacSentenceText || "",
        translatedText: currentValues.validatedTextGs || sentence.validatedTextGs || ""
      };

      console.log("[handleValidate] Request:", requestBody);

      const result = await apiClient(
        `${BASE_URL}api/devUser/validate?devUserId=${userId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          credentials: "include",
          body: JSON.stringify(requestBody),
        },
        navigate
      );

      console.log("[handleValidate] Response:", result);

      if (result.status === "success") {
        toast.success("Validation updated successfully");
        setEditedSentences(prev => {
          const newSet = new Set(prev);
          newSet.delete(sentence.sentenceId);
          return newSet;
        });
        setValidatedSentences(prev => new Set([...prev, sentence.sentenceId]));
        setSentences(prev =>
          prev.map(s =>
            s.sentenceId === sentence.sentenceId
              ? {
                ...s,
                validationStatusDev: "VALIDATED",
                devSentenceText: currentValues.cdacSentenceText || sentence.cdacSentenceText || "",
                validatedTextDev: currentValues.validatedTextGs || sentence.validatedTextGs || ""
              }
              : s
          )
        );
      } else {
        toast.error(result.message || "Validation failed");
      }
    } catch (error) {
      console.error("[handleValidate] Error:", error);
      toast.error("Validation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (sentence) => {
    if (finalCommitCompleted || loading) return;
    setLoading(true);

    try {
      const result = await apiClient(
        `${BASE_URL}api/devUser/rejectSentence?sentenceId=${sentence.sentenceId}&devUserId=${userId}&sourceId=2&targetId=${sentence.targetLanguageId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          credentials: "include",
        },
        navigate
      );

      console.log("[handleReject] Response:", result);

      if (result.success) {
        setReloadTrigger(prev => prev + 1);
        toast.success("Sentence rejected successfully");
      } else {
        toast.error(result.message || "Reject failed");
      }
    } catch (error) {
      console.log("[handleReject] Error:", error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };


  const handleFinalCommit = async () => {
    if (!selectedAssignedFileId || !userId) {
      toast.error("Missing required data for final commit");
      return;
    }
    try {
      setLoading(true);
      const files = selectedAssignedFolderId ? (assignedFiles[selectedAssignedFolderId] || []) : [];
      const file = files.find(f => f.fileId === selectedAssignedFileId);
      if (!file) {
        toast.error("File data not found");
        return;
      }
      const finalTargetLanguageId = targetLanguageId || file.targetId;
      if (!finalTargetLanguageId) {
        toast.error("Target language ID not found");
        return;
      }
      const requestBody = {
        fileId: selectedAssignedFileId,
        sourceLanguageId: file.sourceId || 2,
        targetLanguageId: finalTargetLanguageId,
        devUserId: parseInt(userId)
      };
      const result = await apiClient(
        `${BASE_URL}api/devUser/mark-validation-completed`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          credentials: "include",
          body: JSON.stringify(requestBody),
        },
        navigate
      );
      console.log("[handleFinalCommit] Response:", result);
      if (result.status === "success") {
        toast.success(result.message || "Validation work marked as completed successfully");
        setFinalCommitCompleted(true);
        setReloadTrigger(prev => prev + 1);
      } else {
        toast.error(result.message || "Failed to mark validation as completed");
      }
    } catch (error) {
      console.error("[handleFinalCommit] Error:", error);
      toast.error("Error marking validation as completed");
    } finally {
      setLoading(false);
    }
  };

  const handleFinalCommitClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmFinalCommit = () => {
    setShowConfirmDialog(false);
    handleFinalCommit();
  };

  const handleCancelFinalCommit = () => {
    setShowConfirmDialog(false);
  };

  const allSentencesValidated = sentences.length > 0 && sentences.every(sentence =>
    sentence.validationStatusDev === "VALIDATED"
  );

  // Memoize sorted sentences
  const sortedSentences = useMemo(() => {
    if (clickReject) return rejectedSentences;
    return [...sentences]
  }, [sentences, rejectedSentences, clickReject]);

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="flex-1">
        <div className="w-full p-4 sm:p-6 lg:p-8">
          {!selectedAssignedFileId ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-gray-500 text-sm font-['Semantic/Strong']">
                Please select a file to validate.
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 sm:px-6 lg:px-8 py-4">
                <h2 className="text-xl font-['Semantic/Strong'] text-center font-bold mb-2">
                  File Name: <span className="text-2xl font-['Semantic/Strong'] text-center text-[#ec7026] underline font-bold">{fileName}</span>
                </h2>

                {finalCommitCompleted || isFileAlreadyValidated && (
                  <div className="bg-blue-100 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-4 font-['Semantic/Strong']">
                    <p>This file has already been validated and committed. All actions are disabled.</p>
                  </div>
                )}
                <div className="flex items-center mb-4">
                  <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${!clickReject ? "bg-[#ec7026] text-white" : "bg-white text-[#ec7026] hover:bg-gray-50"} transition-colors duration-200`}
                      onClick={() => setClickReject(false)}
                    >
                      All Sentences
                    </button>
                    <button
                      className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${clickReject ? "bg-[#ec7026] text-white" : "bg-white text-[#ec7026] hover:bg-gray-50"} transition-colors duration-200`}
                      onClick={() => setClickReject(true)}
                    >
                      Rejected Sentences
                    </button>
                  </div>
                </div>
                {loading ? (
                  <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                  </div>
                ) : (clickReject ? rejectedSentences : sortedSentences).length === 0 ? (
                  <div className="p-6">
                    {clickReject ? (
                      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg font-['Semantic/Strong']">
                        <p>No rejected sentences found.</p>
                        <p className="mt-2 text-sm">Please try again later or contact support.</p>
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 font-['Semantic/Strong']">
                        No sentences available
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-300">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border border-gray-200 px-3 py-2 text-center font-bold text-gray-700 text-base w-[5%]">Sr. No.</th>
                            <th className="border border-gray-200 px-3 py-2 text-center font-bold text-gray-700 text-base w-[15%]">
                              CDAC Source Text
                            </th>
                            <th className="border border-gray-200 px-3 py-2 text-center font-bold text-gray-700 text-base w-[30%]">
                              Dev Source Text (English)
                            </th>
                            <th className="border border-gray-200 px-3 py-2 text-center font-bold text-gray-700 text-base w-[15%]">
                              GS Translated Text ({`${languageIdToName[targetLanguageId]}`})
                            </th>
                            <th className="border border-gray-200 px-3 py-2 text-center font-bold text-gray-700 text-base w-[30%]">
                              Dev Translated Text ({`${languageIdToName[targetLanguageId]}`})
                            </th>
                            <th className="border border-gray-200 px-3 py-2 text-center font-bold text-gray-700 text-base w-[10%]">
                              Validation Status
                            </th>
                            {!clickReject && (
                              <th className="border border-gray-200 px-3 py-2 text-center font-bold text-gray-700 text-base w-[10%]">
                                Validate
                              </th>
                            )}
                          </tr>
                        </thead>

                        <tbody>
                          {(clickReject ? rejectedSentences : sortedSentences).map((sentence, index) => {
                            const currentValues = inputValues[sentence.sentenceId] || {};
                            const isDisabled = validatedSentences.has(sentence.sentenceId) || finalCommitCompleted;

                            return (
                              <tr key={sentence.sentenceId || index} className="hover:bg-gray-50 align-top">
                                {/* Sr. No. */}
                                <td className="border border-gray-300 px-3 py-2 text-center w-[60px]">
                                  {index + 1}
                                </td>

                                {/* CDAC Source Text */}
                                <td className="border border-gray-300 px-3 py-2 text-sm text-gray-800 text-left align-top w-[250px]">
                                  <div className="overflow-hidden whitespace-pre-wrap leading-5 min-h-[80px] break-words">
                                    {sentence.cdacSentenceText || "-"}
                                  </div>
                                </td>

                                {/* Dev Source Text */}
                                <td className="border border-gray-300 px-3 py-2 w-[250px]">
                                  {clickReject || finalCommitCompleted || isFileAlreadyValidated ? (
                                    <span className="block overflow-hidden whitespace-pre-wrap leading-5 min-h-[80px] break-words font-['Semantic/Strong'] text-gray-800 bg-gray-50 rounded-lg p-2 border border-gray-200">
                                      {currentValues.cdacSentenceText ||
                                        sentence.devSentenceText ||
                                        sentence.cdacSentenceText ||
                                        "-"}
                                    </span>
                                  ) : (
                                    <textarea
                                      className={`w-full min-h-[80px] border border-gray-300 rounded-lg p-2 text-sm font-['Semantic/Strong']
      focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200`}
                                      value={
                                        currentValues.cdacSentenceText !== undefined
                                          ? currentValues.cdacSentenceText
                                          : (sentence.devSentenceText || sentence.cdacSentenceText || "")
                                      }
                                      onChange={(e) =>
                                        handleEditChange(sentence.sentenceId, "cdacSentenceText", e.target.value)
                                      }
                                      onPaste={(e) => {
                                        e.preventDefault();
                                        return false;
                                      }}
                                    />
                                  )}

                                </td>

                                {/* GS Translated Text */}
                                <td className="border border-gray-300 px-3 py-2 text-sm text-gray-800 text-left align-top w-[250px]">
                                  <div className="overflow-hidden whitespace-pre-wrap leading-5 min-h-[80px] break-words">
                                    {sentence.validatedTextGs || "-"}
                                  </div>
                                </td>

                                {/* Dev Translated Text */}
                                <td className="border border-gray-300 px-3 py-2 w-[250px]">
                                  {clickReject || finalCommitCompleted || isFileAlreadyValidated ? (
                                    <span className="block overflow-hidden whitespace-pre-wrap leading-5 min-h-[80px] break-words text-left font-['Semantic/Strong'] text-gray-800 bg-gray-50 rounded-lg p-2 border border-gray-200">
                                      {currentValues.validatedTextGs ||
                                        sentence.validatedTextDev ||
                                        sentence.validatedTextGs ||
                                        "-"}
                                    </span>
                                  ) : (
                                    <textarea
                                      className={`w-full min-h-[80px] border border-gray-300 rounded-lg p-2 text-sm font-['Semantic/Strong']
      focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200`}
                                      value={
                                        currentValues.validatedTextGs !== undefined
                                          ? currentValues.validatedTextGs
                                          : (sentence.validatedTextDev || sentence.validatedTextGs || "")
                                      }
                                      onChange={(e) =>
                                        handleEditChange(sentence.sentenceId, "validatedTextGs", e.target.value)
                                      }
                                      onPaste={(e) => {
                                        e.preventDefault();
                                        return false;
                                      }}
                                    />
                                  )}

                                </td>


                                {/* Validation Status */}
                                <td className="border border-gray-300 px-3 py-2 text-center align-top">
                                  <span
                                    className={`px-2 py-1 rounded text-xs font-medium ${sentence.validationStatusDev === "VALIDATED"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-yellow-100 text-yellow-800"
                                      }`}
                                  >
                                    {sentence.validationStatusDev || "PENDING"}
                                  </span>
                                </td>

                                {/* Validate / Reject Buttons */}
                                {!clickReject && (
                                  <td className="border border-gray-300 text-center align-top p-2">
                                    <div className="flex flex-col justify-between items-center gap-1 h-full min-h-[80px] py-2">
                                      <button
                                        className={`px-3 py-1 rounded text-white w-[90%] ${editedSentences.has(sentence.sentenceId) ||
                                          ["Edited", "IN_PROGRESS"].includes(sentence.validationStatusDev)
                                          ? "bg-green-500 hover:bg-green-600"
                                          : "bg-gray-400 cursor-not-allowed"
                                          }`}
                                        onClick={() => handleValidate(sentence)}
                                        disabled={isDisabled}
                                      >
                                        {["Edited", "IN_PROGRESS"].includes(sentence.validationStatusDev)
                                          ? "Validate"
                                          : "Validated"}
                                      </button>

                                      <button
                                        className={`px-3 py-1 rounded text-white w-[90%] ${editedSentences.has(sentence.sentenceId) ||
                                          ["Edited", "IN_PROGRESS"].includes(sentence.validationStatusDev)
                                          ? "bg-red-500 hover:bg-red-600"
                                          : "bg-gray-400 cursor-not-allowed"
                                          }`}
                                        onClick={() => handleReject(sentence)}
                                        disabled={isDisabled}
                                      >
                                        Reject
                                      </button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                    </div>
                    <div className="flex justify-end mt-6">
                      <div className="flex flex-col items-end">
                        <div className="mb-2 text-sm text-gray-600">
                          {(clickReject ? rejectedSentences : sortedSentences).length > 0
                            ? `${(clickReject ? rejectedSentences : sortedSentences).filter((s) => s.validationStatusDev === "VALIDATED").length} of ${(clickReject ? rejectedSentences : sortedSentences).length} sentences validated`
                            : "No sentences loaded"}
                        </div>
                        {!clickReject && (
                          <button
                            className={`px-6 py-2 rounded-lg font-semibold shadow transition-colors ${fileStatus === "DEV_VALIDATED" || fileStatus === "DEV_COMPLETED"
                              ? finalCommitCompleted
                                ? "bg-green-600 text-white cursor-not-allowed"
                                : !allSentencesValidated
                                  ? "bg-green-600 text-white cursor-not-allowed"
                                  : "bg-gray-400 text-white cursor-not-allowed"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                              }`}
                            onClick={handleFinalCommitClick}
                            disabled={fileStatus === "DEV_VALIDATED" || loading || (!allSentencesValidated || finalCommitCompleted)}
                          >
                            {loading
                              ? "Processing..."
                              : fileStatus === "DEV_VALIDATED" || fileStatus === "DEV_COMPLETED"
                                ? "Final Commit Completed"
                                : "Final Commit"}
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {showConfirmDialog && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                      <h3 className="text-lg font-semibold mb-4">Confirm Final Commit</h3>
                      <p className="text-gray-600 mb-6">
                        Are you sure you want to submit all validated sentences? This action cannot be undone.
                      </p>
                      <div className="flex justify-end space-x-3">
                        <button
                          className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                          onClick={handleCancelFinalCommit}
                        >
                          Cancel
                        </button>
                        <button
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                          onClick={handleConfirmFinalCommit}
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {popup.isOpen && renderPopup()}
                {loading && (
                  <div className="flex justify-center items-center mt-4">
                    <span className="loader"></span> {/* Replace with your spinner component */}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SentenceValidationTableDevnagriValidator;